
import React, { useState, useEffect } from 'react';
import { DateTime } from "luxon";
import Header from '../Header/Hearder';
import './Appointment.css';
import axiosInstance from "../../lib/axiosInstance";
import SlotPicker from "./SlotPicker";

function Appointment({ branches = [], selectedBranchId = "", onBranchChange }) {
    const PAGE_SIZE = 10;
    const initialNewAppt = {
        patientId: "",
        doctorId: "",
        slotId: "",
        slotStartISO: "",
        date: "",
        time: "",
        durationMinutes: 30,
        notes: "",
    };
    // Appointments page is "Today" by default; use calendar to view other dates.
    const TABS = ['Past', 'Today', 'Upcoming'];
    const [selectedTab, setSelectedTab] = useState('Today');
    const [currentPage, setCurrentPage] = useState(1);

    const [appointments, setAppointments] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
    const [apptLoading, setApptLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [slotRefreshKey, setSlotRefreshKey] = useState(0);
    const [addError, setAddError] = useState("");
    const [addLoading, setAddLoading] = useState(false);
    const [patientSearch, setPatientSearch] = useState("");
    const [tableSearch, setTableSearch] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [newAppt, setNewAppt] = useState(initialNewAppt);

    const [clinicTz, setClinicTz] = useState(process.env.REACT_APP_CLINIC_TZ || "Asia/Kolkata");
    const minAppointmentDate = DateTime.now().setZone(clinicTz).toISODate();

    useEffect(() => {
        if (!selectedBranchId) return;
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranchId, selectedTab, selectedDate, clinicTz]);

    useEffect(() => {
      setCurrentPage(1);
    }, [selectedTab, selectedBranchId]);

    function handleTabChange(tab) {
      setSelectedTab(tab);
      setSelectedDate("");
      setTableSearch("");
      setCurrentPage(1);
    }

    // Default: show only today's appointments.
    useEffect(() => {
      if (selectedTab !== "Today") setSelectedTab("Today");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!showAdd) return;
      const selected = patients.find((p) => p._id === newAppt.patientId || p.id === newAppt.patientId);
      if (selected) {
        setPatientSearch(`${selected.name || ""}${selected.phone ? ` (${selected.phone})` : ""}`);
      } else if (!newAppt.patientId) {
        setPatientSearch("");
      }
    }, [showAdd, newAppt.patientId, patients]);

    // Close open row action menu on outside click / Escape.
    useEffect(() => {
        const handleDocClick = (e) => {
            if (!openMenuId) return;
            if (e.target && e.target.closest && e.target.closest(".row-menu-wrap")) return;
            setOpenMenuId(null);
        };
        const handleEsc = (e) => {
            if (e.key === "Escape") setOpenMenuId(null);
        };
        document.addEventListener("click", handleDocClick);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("click", handleDocClick);
            document.removeEventListener("keydown", handleEsc);
        };
    }, [openMenuId]);

    // try to fetch clinic tz from server so frontend & backend agree
    useEffect(() => {
        let mounted = true;
        axiosInstance
            .get("/config")
            .then((r) => {
                const data = r && r.data ? r.data : r;
                if (mounted && data && data.clinicTz) setClinicTz(data.clinicTz);
            })
            .catch(() => {
                // ignore, we'll fall back to env/default
            });
        return () => {
            mounted = false;
        };
    }, []);

    async function fetchList() {
        if (!selectedBranchId) {
            setAppointments([]);
            setDoctors([]);
            return;
        }
        setApptLoading(true);
        try {
            const now = DateTime.now().setZone(clinicTz);
            let dateFrom, dateTo;

            if (selectedDate) {
                const day = DateTime.fromISO(String(selectedDate)).setZone(clinicTz);
                if (day.isValid) {
                    dateFrom = day.startOf("day").toUTC().toISO();
                    dateTo = day.endOf("day").toUTC().toISO();
                }
            } else if (selectedTab === "Past") {
                dateFrom = now.minus({ days: 90 }).startOf("day").toUTC().toISO();
                dateTo = now.minus({ days: 1 }).endOf("day").toUTC().toISO();
            } else if (selectedTab === "Upcoming") {
                dateFrom = now.plus({ days: 1 }).startOf("day").toUTC().toISO();
                dateTo = now.plus({ days: 90 }).endOf("day").toUTC().toISO();
            } else {
                // Today
                dateFrom = now.startOf("day").toUTC().toISO();
                dateTo = now.endOf("day").toUTC().toISO();
            }

            const apptParams = {
                branchId: selectedBranchId,
                page: 1,
                limit: 500,
                dateFrom,
                dateTo,
                _ts: Date.now(),
            };
            const [aRes, dRes, pActiveRes, pInactiveRes] = await Promise.all([
                axiosInstance.get("/appointments", { params: apptParams }),
                axiosInstance.get("/doctor-branches", { params: { branchId: selectedBranchId, isActive: true, _ts: apptParams._ts } }),
                axiosInstance.get("/patients", { params: { branchId: selectedBranchId, page: 1, limit: 1000, isActive: true, _ts: apptParams._ts } }),
                axiosInstance.get("/patients", { params: { branchId: selectedBranchId, page: 1, limit: 1000, isActive: false, _ts: apptParams._ts } }),
            ]);
            const aData = aRes && aRes.data ? aRes.data : aRes;
            setAppointments(aData && aData.items ? aData.items : (Array.isArray(aData) ? aData : []));
            const mData = dRes && dRes.data ? dRes.data : dRes;
            const maps = mData && mData.items ? mData.items : (Array.isArray(mData) ? mData : []);
            const rawDocs = maps.map((m) => m.doctorId).filter(Boolean);
            const seenDocs = new Set();
            const uniqDocs = [];
            for (const d of rawDocs) {
                const id = d && (d._id || d.id);
                const key = id != null ? String(id) : "";
                if (!key || seenDocs.has(key)) continue;
                seenDocs.add(key);
                uniqDocs.push(d);
            }
            setDoctors(uniqDocs);
            const activeData = pActiveRes && pActiveRes.data ? pActiveRes.data : pActiveRes;
            const inactiveData = pInactiveRes && pInactiveRes.data ? pInactiveRes.data : pInactiveRes;
            const activeItems = activeData && activeData.items ? activeData.items : (Array.isArray(activeData) ? activeData : []);
            const inactiveItems = inactiveData && inactiveData.items ? inactiveData.items : (Array.isArray(inactiveData) ? inactiveData : []);
            const mergedPatients = [...activeItems, ...inactiveItems].filter((p, idx, arr) => {
                const id = p && (p._id || p.id);
                if (!id) return true;
                return arr.findIndex((x) => (x && (x._id || x.id)) === id) === idx;
            });
            setPatients(mergedPatients);
        } catch (err) {
            console.error("fetch lists error", err);
        } finally {
            setApptLoading(false);
        }
    }

    function closeAddModal() {
      setShowAdd(false);
      setAddError("");
      setPatientSearch("");
      setNewAppt(initialNewAppt);
    }

    function getCurrentUserId() {
        try {
            const token = localStorage.getItem("token");
            if (!token) return null;
            const parts = token.split(".");
            if (parts.length < 2) return null;
            const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
            return payload.id || payload.sub || null;
        } catch (e) {
            return null;
        }
    }

    // Tab filtering
    const filteredAppointments = appointments
      .filter((a) => {
          const dateVal = a.appointmentDate || a.date || a.createdAt;
          if (!dateVal) return false;
          const dt = DateTime.fromISO(String(dateVal), { setZone: true }).setZone(clinicTz);
          if (!dt.isValid) return false;
          const now = DateTime.now().setZone(clinicTz);
          if (selectedDate) return dt.toISODate() === selectedDate;
          if (selectedTab === "Today") return dt.toISODate() === now.toISODate();
          if (selectedTab === "Past") return dt < now.startOf("day");
          if (selectedTab === "Upcoming") return dt >= now.plus({ days: 1 }).startOf("day");
          return true;
      })
      .filter((a) => {
          const q = String(tableSearch || "").trim().toLowerCase();
          if (!q) return true;
          const patient = a.patientId && a.patientId.name ? a.patientId : { name: a.patient || "", phone: a.patientPhone || "", email: a.patientEmail || "" };
          const doctor = a.doctorId && a.doctorId.name ? a.doctorId : { name: a.doctor || "" };
          const status = String(a.status || a.statusText || "");
          const dateVal = a.appointmentDate || a.date || a.createdAt || "";
          const dateStr = dateVal ? DateTime.fromISO(String(dateVal), { setZone: true }).setZone(clinicTz).toLocaleString(DateTime.DATETIME_MED) : "";
          const hay = [
              patient.name,
              patient.phone,
              patient.email,
              doctor.name,
              status,
              dateStr,
          ].map((x) => String(x || "").toLowerCase()).join(" ");
          return hay.includes(q);
      })
      .sort((a, b) => {
          const ad = new Date(a.appointmentDate || a.date || a.createdAt || 0).getTime();
          const bd = new Date(b.appointmentDate || b.date || b.createdAt || 0).getTime();
          // Past: most recent first; Today & Upcoming: earliest first
          return selectedTab === "Past" ? bd - ad : ad - bd;
      });
    const totalPages = Math.max(1, Math.ceil(filteredAppointments.length / PAGE_SIZE));
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const paginatedAppointments = filteredAppointments.slice(pageStart, pageStart + PAGE_SIZE);
    const goToPatientRecords = () => {
      window.history.pushState({}, "", "/patientrecords?create=1");
      window.dispatchEvent(new PopStateEvent("popstate"));
    };
    const filteredPatients = patients.filter((p) => {
      const q = String(patientSearch || "").trim().toLowerCase();
      if (!q) return true;
      const name = String(p.name || "").toLowerCase();
      const phone = String(p.phone || "").toLowerCase();
      const email = String(p.email || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || email.includes(q);
    });
    const selectedPatient = patients.find((p) => (p._id || p.id) === newAppt.patientId);

    function getPatientRegistrationNumberForAppointment(appt) {
      const p = appt?.patientId;
      const direct =
        p && typeof p === "object"
          ? (p.registrationNumber || p.registrationNo || p.regNo || "")
          : "";
      if (direct) return String(direct);
      const pid =
        p && typeof p === "object"
          ? (p._id || p.id)
          : (appt?.patientId || appt?.patient);
      if (!pid) return "";
      const match = (patients || []).find((x) => String(x._id || x.id) === String(pid));
      return match && (match.registrationNumber || match.registrationNo || match.regNo) ? String(match.registrationNumber || match.registrationNo || match.regNo) : "";
    }

    return (
        <div className="appointments-container">
            <Header branches={branches} selectedBranchId={selectedBranchId} onBranchChange={onBranchChange} />

            <div className="appointments-main">
                {/* Header: tabs + per-tab filters */}
                <div className="appointments-header">
                    <div className="simple-tabs">
                        {TABS.map((tab) => (
                            <button
                                key={tab}
                                className={`tab-btn ${selectedTab === tab ? 'active' : ''}`}
                                onClick={() => handleTabChange(tab)}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div className="header-actions-below">
                        <div className="header-actions">
                            <div className="appt-search-container">
                              <input
                                className="appt-search-input"
                                value={tableSearch}
                                onChange={(e) => {
                                  setTableSearch(e.target.value);
                                  setCurrentPage(1);
                                }}
                                placeholder={`Search ${selectedTab.toLowerCase()} appointments (patient, doctor, phone, status...)`}
                              />
                            </div>
                            {selectedTab !== "Today" && (
                              <div className="appt-date-filter">
                                <input
                                  className="appt-date-input"
                                  type="date"
                                  value={selectedDate}
                                  onChange={(e) => {
                                    setSelectedDate(e.target.value);
                                    setCurrentPage(1);
                                  }}
                                  {...(selectedTab === "Upcoming"
                                    ? { min: DateTime.now().setZone(clinicTz).plus({ days: 1 }).toISODate() }
                                    : { max: DateTime.now().setZone(clinicTz).minus({ days: 1 }).toISODate() }
                                  )}
                                />
                                {!!selectedDate && (
                                  <button
                                    type="button"
                                    className="appt-date-clear"
                                    onClick={() => {
                                      setSelectedDate("");
                                      setCurrentPage(1);
                                    }}
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                            )}
                            <button className="add-appointment-btn" type="button" onClick={() => setShowAdd(true)}>+ Add Appointment</button>
                        </div>
                    </div>
                </div>

                {/* APPOINTMENTS TABLE */}
                <div className="appointments-table">
                    <div className="table-header">
                        <div className="header-cell patient-col">Patient</div>
                        <div className="header-cell regno-col">Registration No</div>
                        <div className="header-cell doctor-col">Doctor</div>
                        <div className="header-cell date-col">Date & Time</div>
                        <div className="header-cell status-col">Status</div>
                        <div className="header-cell actions-col">Actions</div>
                    </div>


                    <div className='table-app'>
                        <div className="table-body">
                        {apptLoading ? (
                            <div className="table-row"><div className="table-cell">Loading...</div></div>
                        ) : paginatedAppointments.length === 0 ? (
                            <div className="table-row"><div className="table-cell">No {selectedTab.toLowerCase()} appointments found.</div></div>
                        ) : paginatedAppointments.map((appointment) => {
                            const pid = appointment._id || appointment.id;
                            const patient = appointment.patientId && appointment.patientId.name ? appointment.patientId : { name: appointment.patient || "Unknown", phone: appointment.patientPhone || "" };
                            const doctor = appointment.doctorId && appointment.doctorId.name ? appointment.doctorId : { name: appointment.doctor || "Unknown" };
                            const regNo = getPatientRegistrationNumberForAppointment(appointment);
                            const ad = appointment.appointmentDate ? (typeof appointment.appointmentDate === "string" ? appointment.appointmentDate : appointment.appointmentDate.toString()) : null;
                            const dateStr = ad ? DateTime.fromISO(ad).setZone(clinicTz).toLocaleString(DateTime.DATE_MED) : (appointment.date || "");
                            const timeStr = ad ? DateTime.fromISO(ad).setZone(clinicTz).toLocaleString(DateTime.TIME_SIMPLE) : (appointment.time || "");
                            const status = (appointment.status || appointment.statusText || "scheduled");
                            const statusKey = String(status || "").toLowerCase();
                            const canUnblockSlot = ["booked", "confirmed", "reserved", "scheduled"].includes(statusKey);
                            return (
                                <div key={pid} className="table-row">
                                    <div className="table-cell patient-cell">
                                        <div className="patient-avatar">
                                            {(patient.name || "").split(' ').map(n => n[0]).join('').slice(0,2)}
                                        </div>
                                        <div className="appt-patient-info">
                                            <div className="appt-patient-name">{patient.name}</div>
                                            <div className="appt-patient-phone">{patient.phone || ""}</div>
                                        </div>
                                    </div>

                                    <div className="table-cell regno-cell">
                                      <div className="appt-regno">{regNo || "-"}</div>
                                    </div>

                                    <div className="table-cell doctor-cell">
                                        <div className="doctor-name">{doctor.name}</div>
                                    </div>

                                    <div className="table-cell date-cell">
                                        <div className="date">{dateStr}</div>
                                        <div className="time">{timeStr}</div>
                                    </div>

                                    <div className="table-cell status-cell">
                                        {(() => {
                                          const cls = (String(status || "scheduled").toLowerCase() || "scheduled").replace(/\s+/g, "-");
                                          return (
                                            <div className={`status-badge ${cls}`}>
                                              {String(status).toUpperCase()}
                                            </div>
                                          );
                                        })()}
                                    </div>
                                    <div className="table-cell actions-cell">
                                      <div className="row-menu-wrap" onClick={(e)=>e.stopPropagation()}>
                                        <button
                                          type="button"
                                          className="row-menu-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenMenuId(openMenuId === pid ? null : pid);
                                          }}
                                        >
                                          ⋯
                                        </button>
                                        {openMenuId === pid && (
                                          <div className="row-menu">
                                            {statusKey === "reserved" ? (
                                              <button
                                                type="button"
                                                className="row-menu-item"
                                                onClick={async () => {
                                                  setOpenMenuId(null);
                                                  try {
                                                    await axiosInstance.post(`/appointments/${pid}/confirm`);
                                                    await fetchList();
                                                  } catch (err) {
                                                    alert((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Confirm failed");
                                                  }
                                                }}
                                              >
                                                Confirm
                                              </button>
                                            ) : (
                                              <>
                                                <button
                                                  type="button"
                                                  className="row-menu-item"
                                                  onClick={async () => {
                                                    setOpenMenuId(null);
                                                    try {
                                                      await axiosInstance.put(`/appointments/${pid}`, { status: "confirmed" });
                                                      await fetchList();
                                                    } catch (err) {
                                                      alert((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Update failed");
                                                    }
                                                  }}
                                                >
                                                  Mark confirmed
                                                </button>
                                              </>
                                            )}
                                            {canUnblockSlot && (
                                              <button
                                                type="button"
                                                className="row-menu-item"
                                                onClick={async () => {
                                                  setOpenMenuId(null);
                                                  if (!window.confirm("Cancel this appointment and release the booked slot?")) return;
                                                  try {
                                                    const res = await axiosInstance.post(`/appointments/${pid}/unblock-slot`);
                                                    const data = res && res.data ? res.data : res;
                                                    await fetchList();
                                                    setSlotRefreshKey((k) => k + 1);
                                                    if (data && data.slotReleased === false) {
                                                      alert("Appointment cancelled. No linked slot found to release.");
                                                    }
                                                  } catch (err) {
                                                    alert((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Unblock slot failed");
                                                  }
                                                }}
                                              >
                                                Unblock Slot
                                              </button>
                                            )}
                                            <button
                                              type="button"
                                              className="row-menu-item delete"
                                              onClick={async () => {
                                                setOpenMenuId(null);
                                                if (!window.confirm("Delete appointment?")) return;
                                                try {
                                                  await axiosInstance.delete(`/appointments/${pid}`);
                                                  await fetchList();
                                                } catch (err) {
                                                  alert((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Delete failed");
                                                }
                                              }}
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    </div>
                   
                </div>
                <div className="appointments-pagination">
                  <div className="appointments-pagination-info">
                    Page {Math.min(currentPage, totalPages)} of {totalPages} {filteredAppointments.length ? `(${filteredAppointments.length} records)` : ""}
                  </div>
                  <div className="appointments-pagination-actions">
                    <button
                      type="button"
                      className="page-btn"
                      disabled={currentPage <= 1 || apptLoading}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="page-btn"
                      disabled={currentPage >= totalPages || apptLoading}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>

                {/* ADD APPOINTMENT MODAL */}
                {showAdd && (
                    <div className="modal-overlay" onClick={closeAddModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h3>Create appointment</h3>
                            {addError && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{addError}</div>}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div style={{ position: "relative" }}>
                                  <input
                                    placeholder="Search patient by name/phone/email"
                                    value={patientSearch}
                                    onChange={(e) => {
                                      setPatientSearch(e.target.value);
                                      setNewAppt({ ...newAppt, patientId: "" });
                                    }}
                                  />
                                  {patientSearch.trim() && !selectedPatient && (
                                    <div style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 20, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, marginTop: 4, maxHeight: 180, overflowY: "auto" }}>
                                      {filteredPatients.length > 0 ? (
                                        filteredPatients.slice(0, 10).map((p) => (
                                          <button
                                            key={p._id || p.id}
                                            type="button"
                                            style={{ width: "100%", textAlign: "left", padding: "8px 10px", border: "none", background: "transparent", cursor: "pointer" }}
                                            onClick={() => {
                                              const id = p._id || p.id;
                                              setNewAppt({ ...newAppt, patientId: id });
                                              setPatientSearch(`${p.name || ""}${p.phone ? ` (${p.phone})` : ""}`);
                                            }}
                                          >
                                            {p.name} {p.phone ? `(${p.phone})` : ""}
                                          </button>
                                        ))
                                      ) : (
                                        <div style={{ padding: "10px", color: "#6b7280", fontSize: 13 }}>
                                          <div style={{ marginBottom: 8 }}>
                                            Patient not found.
                                          </div>
                                          <button
                                            type="button"
                                            className="create-btn"
                                            style={{ padding: "6px 10px", borderRadius: 8, fontSize: 12 }}
                                            onClick={goToPatientRecords}
                                          >
                                            Create a Record
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <select value={newAppt.doctorId} onChange={(e) => setNewAppt({ ...newAppt, doctorId: e.target.value, slotId: "", slotStartISO: "" })}>
                                    <option value="">Select doctor</option>
                                    {doctors.map(d => <option key={d._id || d.id} value={d._id || d.id}>{d.name}</option>)}
                                </select>
                                <input
                                  type="date"
                                  min={minAppointmentDate}
                                  value={newAppt.date}
                                  onChange={(e) => setNewAppt({ ...newAppt, date: e.target.value, slotId: "", slotStartISO: "" })}
                                />
                                <input
                                  type="time"
                                  value={newAppt.time}
                                  onChange={(e) => setNewAppt({ ...newAppt, time: e.target.value, slotId: "", slotStartISO: "" })}
                                  readOnly
                                  title="Select an available slot below"
                                />
                                <input type="number" min="5" value={newAppt.durationMinutes} onChange={(e) => setNewAppt({ ...newAppt, durationMinutes: Number(e.target.value) })} />
                                <input placeholder="Notes" value={newAppt.notes} onChange={(e) => setNewAppt({ ...newAppt, notes: e.target.value })} />
                            </div>
                            {/* Slot picker - shows clinic-local available slots for selected doctor & date */}
                            <div style={{ marginTop: 12 }}>
                              {selectedBranchId && newAppt.doctorId && newAppt.date ? (
                                <SlotPicker
                                  doctorId={newAppt.doctorId}
                                  branchId={selectedBranchId}
                                  date={newAppt.date}
                                  refreshKey={slotRefreshKey}
                                  durationMinutes={newAppt.durationMinutes}
                                  onSelect={(slot) => {
                                    // set date/time fields from selected slot
                                    const dt = DateTime.fromISO(slot.startISO || slot.start || slot.startTime);
                                    setNewAppt({
                                      ...newAppt,
                                      slotId: slot.slotId || "",
                                      slotStartISO: slot.startISO || slot.start || slot.startTime || "",
                                      date: dt.toISODate(),
                                      time: dt.toFormat("HH:mm"),
                                    });
                                  }}
                                />
                              ) : (
                                <div style={{ color: "#6b7280", marginTop: 8 }}>
                                  {selectedBranchId ? "Select doctor and date to view available slots." : "Select a branch first to load doctor slots."}
                                </div>
                              )}
                            </div>
                            <div style={{ marginTop: 12, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button type="button" className="cancel-btn" onClick={closeAddModal}>Cancel</button>
                                <button type="button" className="create-btn" disabled={addLoading} onClick={async () => {
                                    setAddError("");
                                    if (!newAppt.patientId || !newAppt.doctorId || !newAppt.date) {
                                        setAddError("branch, patient, doctor and date required");
                                        return;
                                    }
                                    if (!newAppt.slotId) {
                                        setAddError("Please select an available slot from the list.");
                                        return;
                                    }
                                    if (!selectedBranchId) {
                                        setAddError("Please select a branch");
                                        return;
                                    }
                                    setAddLoading(true);
                                    try {
                                        const payload = {
                                            patientId: newAppt.patientId,
                                            doctorId: newAppt.doctorId,
                                            branchId: selectedBranchId,
                                            durationMinutes: newAppt.durationMinutes,
                                            notes: newAppt.notes,
                                        };
                                        // Backend now prefers slotId and derives appointment time from slot row.
                                        payload.slotId = newAppt.slotId;
                                        // Keep fallback only if slot ISO exists for compatibility.
                                        if (newAppt.slotStartISO) payload.appointmentDate = newAppt.slotStartISO;
                                        const createdBy = getCurrentUserId();
                                        if (createdBy) payload.createdBy = createdBy;
                                        await axiosInstance.post("/appointments", payload);
                                        closeAddModal();
                                        setSlotRefreshKey((k) => k + 1);
                                        await fetchList();
                                    } catch (err) {
                                        const respErr = err && err.response && err.response.data && err.response.data.error;
                                        if (err && err.response && err.response.status === 409) {
                                            setSlotRefreshKey((k) => k + 1);
                                            setAddError(respErr || "Requested slot already booked or overlaps");
                                        } else if (err && err.response && (err.response.status === 400 || err.response.status === 404)) {
                                            setAddError(respErr || "Invalid data or resource not found");
                                        } else {
                                            setAddError((err && err.message) || "Create failed");
                                        }
                                    } finally {
                                        setAddLoading(false);
                                    }
                                }}>{addLoading ? "Creating..." : "Create"}</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Appointment;
